import { useMemo } from "react";

import { useLevelContext } from "sj-ui-toolkit/src/atlas/contexts/Level/LevelContextProvider";
import { BucketOfLearninRow } from "./BucketOfLearninRow";
import { useUpgradedPathQuestsRowUx } from "../../../../featureFlags";
import { getLevelVariant } from "../../../../helpers/levelVariants";

export const LevelRow = ({ metadata, details, currentPaths = [], completed }) => {
  const { isLoadingLevelDetails } = useLevelContext();

  if (details?.error) {
    return null;
  }

  // For completed levels, show every path (the user is enrolled in them all — that's how
  // they completed it) so the row actually has content. For current levels, hide the paths
  // the user is already enrolled in so the preview only shows new options.
  const filteredDetails = useMemo(() => {
    if (completed) return details;
    if (!currentPaths?.length || !details?.length) return details;

    const enrolledRoleIds = new Set(
      currentPaths.filter(path => path.roleId != null).map(path => path.roleId)
    );

    return details.filter(path => {
      const pathRoleId = path?.id;
      return pathRoleId == null || !enrolledRoleIds.has(pathRoleId);
    });
  }, [details, currentPaths, completed]);

  const isLoading =
    isLoadingLevelDetails ||
    details === undefined ||
    (details !== undefined && filteredDetails === undefined);

  return (
    <BucketOfLearninRow
      loading={isLoading}
      id={metadata.id}
      name={metadata.name}
      description={metadata.description}
      learnins={filteredDetails}
      details={filteredDetails}
      variant={getLevelVariant(metadata)}
      currentPaths={currentPaths}
      type="level"
      viewAllHref={`/levels/${metadata.id}`}
      completed={completed}
    />
  );
};

export const LevelPaths = ({ currentPaths }) => {
  const { levels, levelsLoading, levelDetails } = useLevelContext();
  const upgradedUx = useUpgradedPathQuestsRowUx();

  if (levelsLoading) {
    return null;
  }

  const visibleLevels = levels.filter(level => !["brown", "black"].includes(level.rankName));

  const renderRow = (level, completed) => (
    <LevelRow
      key={level.id}
      metadata={level}
      details={levelDetails[level.id]}
      currentPaths={currentPaths}
      completed={completed}
    />
  );

  // Legacy: flat list, no Current/Completed grouping. The row dispatcher renders the
  // legacy layout (no completion checkmark, no "Show all" button) under the same flag.
  if (!upgradedUx) {
    return <div className="my-8">{visibleLevels.map(level => renderRow(level, false))}</div>;
  }

  const currentLevels = visibleLevels.filter(level => level.status !== "passed");
  const completedLevels = visibleLevels.filter(level => level.status === "passed");

  return (
    <div className="my-8">
      {currentLevels.length > 0 && (
        <section className="mb-8">
          <h2 className="atlas-h4 mb-4">
            {currentLevels.length === 1 ? "Current Level" : "Current Levels"}
          </h2>
          {currentLevels.map(level => renderRow(level, false))}
        </section>
      )}
      {completedLevels.length > 0 && (
        <section>
          <h2 className="atlas-h4 mb-4">
            {completedLevels.length === 1 ? "Completed Level" : "Completed Levels"}
          </h2>
          {completedLevels.map(level => renderRow(level, true))}
        </section>
      )}
    </div>
  );
};
